import React, { useState } from 'react';
import { Box, Typography, Collapse, IconButton } from '@mui/material';
import { styled } from '@mui/system';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningIcon from '@mui/icons-material/Warning';
import { SPACING, RADIUS } from '../../../theme/theme';

// Padding and radius match the dashboard cards (SPACING.md / RADIUS.md) so the
// banner reads as part of the same grid rather than a floating strip.
const ErrorContainer = styled(Box)(() => ({
  width: '100%',
  backgroundColor: '#FFF3CD',
  border: '1px solid #FFEAA7',
  borderRadius: `${RADIUS.md}px`,
  padding: `${SPACING.md}px`,
  marginTop: `${SPACING.md}px`,
  marginBottom: 0,
  direction: 'rtl',
  boxSizing: 'border-box',
}));

const ErrorHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
}));

const ErrorContent = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const ErrorText = styled(Typography)(() => ({
  fontFamily: 'IRANYekanXFaNum',
  fontSize: '14px',
  fontWeight: 500,
  color: '#856404',
  lineHeight: '20px',
}));

const ErrorList = styled(Box)(() => ({
  marginTop: '4px',
  paddingRight: '24px',
}));

const ErrorItem = styled(Typography)(() => ({
  fontFamily: 'IRANYekanXFaNum',
  fontSize: '13px',
  color: '#856404',
  marginBottom: '4px',
  '&:last-child': {
    marginBottom: 0,
  },
}));

interface ErrorBoxProps {
  errors: Record<string, string>;
}

const ErrorBox: React.FC<ErrorBoxProps> = ({ errors }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const errorEntries = Object.entries(errors);
  const hasMultipleErrors = errorEntries.length > 1;
  const firstError = errorEntries[0];

  if (!firstError) return null;

  const handleToggle = () => {
    if (hasMultipleErrors) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <ErrorContainer>
      <ErrorHeader onClick={handleToggle}>
        <ErrorContent>
          <WarningIcon sx={{ color: '#856404', fontSize: '20px' }} />
          <ErrorText>
            {hasMultipleErrors
              ? `${errorEntries.length} دلیل برای رد شدن درخواست شما اعلام گردید.`
              : firstError[1]}
          </ErrorText>
        </ErrorContent>
        {hasMultipleErrors && (
          <IconButton size="small" sx={{ padding: 0 }}>
            {isExpanded ? (
              <ExpandLessIcon sx={{ color: '#856404' }} />
            ) : (
              <ExpandMoreIcon sx={{ color: '#856404' }} />
            )}
          </IconButton>
        )}
      </ErrorHeader>

      {hasMultipleErrors && (
        <Collapse in={isExpanded}>
          <ErrorList>
            {errorEntries.map(([key, message], index) => (
              <ErrorItem key={index}>
                {index + 1}. {message}
              </ErrorItem>
            ))}
          </ErrorList>
        </Collapse>
      )}
    </ErrorContainer>
  );
};

export default ErrorBox;
